import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { customersAPI } from '../services/api';
import { Users, ArrowLeft, Phone, Mail, MapPin, Package, Calendar, Clock, Eye, Truck, ChevronDown, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState({});

  const { data: customerData, isLoading, error } = useQuery(
    ['customer', id],
    () => customersAPI.getById(id)
  );

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error loading customer details</div>;

  const { customer, shipments } = customerData || {};

  if (!customer) {
    return <div className="text-red-600">Customer not found</div>;
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'inquiry': 'bg-gray-100 text-gray-800',
      'box_collection': 'bg-yellow-100 text-yellow-800',
      'box_collection_done': 'bg-green-100 text-green-800',
      'box_delivery': 'bg-blue-100 text-blue-800',
      'box_delivery_done': 'bg-green-100 text-green-800',
      'quick_packing': 'bg-orange-100 text-orange-800',
      'quick_packing_done': 'bg-green-100 text-green-800',
      'shipment': 'bg-purple-100 text-purple-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_shipping': 'bg-blue-100 text-blue-800',
      'in_sl_warehouse': 'bg-purple-100 text-purple-800',
      'shipping_to_customer': 'bg-orange-100 text-orange-800',
      'delivered': 'bg-green-100 text-green-800',
      'fully_paid': 'bg-green-100 text-green-800',
      'partial_paid': 'bg-yellow-100 text-yellow-800',
      'pending_payment': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  // Helper function to format status timestamps
  const getStatusTimestamp = (shipment, statusType) => {
    if (!shipment.statusHistory || shipment.statusHistory.length === 0) {
      return 'N/A';
    }
    
    const statusEntry = shipment.statusHistory.find(h => h.to_status === statusType);
    return statusEntry ? new Date(statusEntry.created_at).toLocaleString() : 'N/A';
  };

  // Helper function to get all status changes with timestamps
  const getAllStatusTimestamps = (shipment) => {
    if (!shipment.statusHistory || shipment.statusHistory.length === 0) {
      return [];
    }
    
    return shipment.statusHistory.map(history => ({
      status: history.to_status,
      timestamp: new Date(history.created_at).toLocaleString(),
      changedBy: history.changed_by_name,
      comment: history.comment
    }));
  };

  // Helper function to get the latest status change
  const getLatestStatusChange = (shipment) => {
    const allChanges = getAllStatusTimestamps(shipment);
    if (allChanges.length === 0) {
      return null;
    }
    
    // Return the most recent status change (last in the array since they're ordered by created_at ASC)
    return allChanges[allChanges.length - 1];
  };

  // Toggle row expansion
  const toggleRowExpansion = (shipmentId) => {
    setExpandedRows(prev => ({
      ...prev,
      [shipmentId]: !prev[shipmentId]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Customers
          </button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{customer.full_name}</h1>
            <p className="text-gray-600">Customer Details & Shipments</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/shipments/new', { state: { customerId: customer.id } })}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Package className="h-4 w-4 mr-2 inline" />
            New Shipment
          </button>
          <button
            onClick={() => navigate(`/customers/${customer.id}/edit`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Customer
          </button>
        </div>
      </div>

      {/* Customer Information Card - Now at the top */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Customer Information
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{customer.full_name}</h3>
                <p className="text-sm text-gray-500">Customer #{customer.id}</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <Phone className="h-4 w-4 mr-3" />
                <span>{customer.phone_number}</span>
              </div>
              {customer.email && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-3" />
                  <span>{customer.email}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 mr-3" />
                <span>Customer since {new Date(customer.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Package className="h-4 w-4 mr-3" />
                <span>Total Shipments: {shipments?.length || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Truck className="h-5 w-5 mr-2" />
            All Shipments ({shipments?.length || 0})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          {shipments && shipments.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shipment Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shipment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status Timeline
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shipments.map((shipment) => (
                  <tr key={shipment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Shipment #{shipment.id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {shipment.pickup_address} → {shipment.delivery_address}
                        </div>
                        <div className="text-sm text-gray-500">
                          {shipment.city_name} • {shipment.route_name}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Created: {new Date(shipment.created_at).toLocaleDateString()}
                        </div>
                        {shipment.delivery_date && (
                          <div className="text-xs text-gray-400">
                            Delivery: {new Date(shipment.delivery_date).toLocaleDateString()}
                          </div>
                        )}
                        {shipment.due_date && (
                          <div className="text-xs text-gray-400">
                            Due: {new Date(shipment.due_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                        {shipment.status?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.shipment_status)}`}>
                        {shipment.shipment_status?.replace('_', ' ') || 'Not Set'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.shipment_payment_status)}`}>
                        {shipment.shipment_payment_status?.replace('_', ' ') || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-500">
                        {/* Show latest status change by default */}
                        {(() => {
                          const latestChange = getLatestStatusChange(shipment);
                          return latestChange ? (
                            <div className="mb-3 p-2 bg-blue-50 rounded-lg border-l-4 border-l-blue-400">
                              <div className="font-medium text-gray-700 mb-1">Latest Update:</div>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(latestChange.status)}`}>
                                  {latestChange.status?.replace('_', ' ') || 'Unknown'}
                                </span>
                                <span className="text-xs text-gray-600 font-medium">{latestChange.timestamp}</span>
                              </div>
                              {latestChange.changedBy && (
                                <div className="text-xs text-gray-500">
                                  Changed by: {latestChange.changedBy}
                                </div>
                              )}
                              {latestChange.comment && (
                                <div className="text-xs text-gray-600 mt-1 italic">
                                  "{latestChange.comment}"
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                              <div className="text-xs text-gray-500">No status history available</div>
                            </div>
                          );
                        })()}
                        
                        {/* Show All Timeline Button */}
                        <button
                          onClick={() => toggleRowExpansion(shipment.id)}
                          className="flex items-center text-blue-600 hover:text-blue-800 mb-2"
                        >
                          {expandedRows[shipment.id] ? (
                            <ChevronDown className="h-4 w-4 mr-1" />
                          ) : (
                            <ChevronRight className="h-4 w-4 mr-1" />
                          )}
                          <span className="font-medium">
                            {expandedRows[shipment.id] ? 'Hide' : 'Show'} All Timeline ({getAllStatusTimestamps(shipment).length} changes)
                          </span>
                        </button>
                        
                        {/* Expandable Full Timeline */}
                        {expandedRows[shipment.id] && (
                          <div className="space-y-2 bg-gray-50 p-3 rounded-lg border">
                            <h4 className="font-medium text-gray-700 mb-2">Complete Status History:</h4>
                            {getAllStatusTimestamps(shipment).map((statusChange, idx) => (
                              <div key={idx} className="border-l-2 border-blue-200 pl-3 py-1">
                                <div className="flex items-center justify-between">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(statusChange.status)}`}>
                                    {statusChange.status?.replace('_', ' ') || 'Unknown'}
                                  </span>
                                  <span className="text-xs text-gray-500">{statusChange.timestamp}</span>
                                </div>
                                {statusChange.changedBy && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Changed by: {statusChange.changedBy}
                                  </div>
                                )}
                                {statusChange.comment && (
                                  <div className="text-xs text-gray-600 mt-1 italic">
                                    "{statusChange.comment}"
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => navigate(`/shipments/${shipment.id}`)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No shipments yet</h3>
              <p className="mt-2 text-sm text-gray-500">
                This customer hasn't made any shipments yet.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/shipments/new', { state: { customerId: customer.id } })}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Package className="h-4 w-4 mr-2" />
                  Create Shipment
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail; 